import React, {useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Lists.module.css';
import StackView from '../listengine/StackEngine';
import QueueView from '../listengine/QueueEngine';
import LinkedListView from '../listengine/LinkedListEngine';
import DoublyLinkedListView from '../listengine/DoublyLinkedListEngine';
import RingBufferView from '../listengine/RingBufferEngine';
import { ListCommand } from '../../../types/ListCommands';

type ScenarioAction = 'addLast' | 'addFirst' | 'removeFirst' | 'removeLast' | 'insertInMiddle' | 'reset';


const CompareLists: React.FC = () => {
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationsRunning, setAnimationsRunning] = useState(0);
  const [lastAction, setLastAction] = useState<ScenarioAction | null>(null);
  const [stackCommand, setStackCommand] = useState<ListCommand | null>(null);
  const [queueCommand, setQueueCommand] = useState<ListCommand | null>(null);
  const [linkedListCommand, setLinkedListCommand] = useState<ListCommand | null>(null);
  const [doublyLinkedListCommand, setDoublyLinkedListCommand] = useState<ListCommand | null>(null);
  const [ringBufferCommand, setRingBufferCommand] = useState<ListCommand | null>(null);
  const TOTAL_COMPONENTS = 5;
  const delay = (ms: number) => new Promise(res => setTimeout(res, ms));
  const animationResolver: React.MutableRefObject<((value?: void) => void) | null> = useRef(null);

  useEffect(() => {
    if (animationsRunning <= 0) {
      setIsAnimating(false);
      if (animationResolver.current) {
        animationResolver.current();
        animationResolver.current = null;
      }
    }
  }, [animationsRunning]);

  const startAnimation = (numComponents = TOTAL_COMPONENTS) => {
    if (isAnimating) return false;
    setIsAnimating(true);
    setAnimationsRunning(numComponents);
    return true;
  };

  const handleAnimationComplete = () => {
    setAnimationsRunning(prev => prev - 1);
  };

 const handleAddLast = (isReplay: boolean = false) => {
    if (!startAnimation()) return;
    if (!isReplay) setLastAction('addLast');
    const randomValue = Math.floor(Math.random() * 100);
    const command: ListCommand = { id: Date.now(), action: 'addLast', value: randomValue };
    setStackCommand(command);
    setQueueCommand(command);
    setLinkedListCommand(command);
    setDoublyLinkedListCommand(command);
    setRingBufferCommand(command);
  };

  const handleAddFirst = (isReplay: boolean = false) => {
    if (!startAnimation(4)) return;
    if (!isReplay) setLastAction('addFirst');
    const randomValue = Math.floor(Math.random() * 100);
    const command: ListCommand = { id: Date.now(), action: 'addFirst', value: randomValue };
    setStackCommand(command);
    setQueueCommand(command);
    setLinkedListCommand(command);
    setDoublyLinkedListCommand(command);
    setRingBufferCommand(null);
  };

  const handleRemoveFromFront = (isReplay: boolean = false) => {
    if (!startAnimation()) return;
    if (!isReplay) setLastAction('removeFirst');
    const command: ListCommand = { id: Date.now(), action: 'removeFirst' };
    setStackCommand(command);
    setQueueCommand(command);
    setLinkedListCommand(command);
    setDoublyLinkedListCommand(command);
    setRingBufferCommand(command);
  };

  const handleRemoveFromEnd = (isReplay: boolean = false) => {
    if (!startAnimation(4)) return;
    if (!isReplay) setLastAction('removeLast');
    const command: ListCommand = { id: Date.now(), action: 'removeLast' };
    setStackCommand(command);
    setQueueCommand(command);
    setLinkedListCommand(command);
    setDoublyLinkedListCommand(command);
    setRingBufferCommand(null); 
  };

  const handleInsertInMiddle = (isReplay: boolean = false) => {
    if (!startAnimation(2)) return;
    if (!isReplay) setLastAction('insertInMiddle');
    const randomValue = Math.floor(Math.random() * 100);
    const command: ListCommand = { id: Date.now(), action: 'insertAt', value: randomValue, index: 2 };
    setLinkedListCommand(command);
    setDoublyLinkedListCommand(command);
    setStackCommand(null);
    setQueueCommand(null);
    setRingBufferCommand(null);
  };

  const handleReset = (isReplay: boolean = false) => {
    if (!startAnimation()) return;
    if (!isReplay) setLastAction('reset');
    const command: ListCommand = { id: Date.now(), action: 'reset' };
    setStackCommand(command);
    setQueueCommand(command);
    setLinkedListCommand(command);
    setDoublyLinkedListCommand(command);
    setRingBufferCommand(command);
  };

  const handleReplay = async () => {
    if (isAnimating || !lastAction) return;
    setIsAnimating(true);

    await new Promise<void>(resolve => {
      animationResolver.current = resolve;
      handleReset(true);
    });
    
    await delay(1);
    await new Promise<void>(resolve => {
      animationResolver.current = resolve;
      switch (lastAction) {
        case 'addLast': handleAddLast(true); break;
        case 'addFirst': handleAddFirst(true); break;
        case 'removeFirst': handleRemoveFromFront(true); break;
        case 'removeLast': handleRemoveFromEnd(true); break;
        case 'insertInMiddle': handleInsertInMiddle(true); break;
        case 'reset': handleReset(true); break; 
        default: resolve(); break;
      }
    });
  };
  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>&larr; Return</button>
      <h1 className={styles.title}>Data Structure Olympics</h1>
             <div className={styles.description}>
            <h2>Description</h2>
            <p>
              Imagine each data structure is a unique athlete competing in a series of performance challenges. The scenario buttons at the top represent different "events" designed to test their specific capabilities.

              Watch closely as they tackle tasks like adding to the front, removing from the end, or inserting into the middle. This showcase is designed to visually demonstrate the fundamental trade-offs, strengths, and weaknesses of each structure, making it clear why choosing the right one is crucial for building fast and efficient software.
            </p>
        </div>

        <div className={styles.useCase}>
            <h2>A Quick Note on "Winning"</h2>
          <ul>
            <li><b>Why don't all structures compete in every event?</b> Some operations, like <b>"Insert in Middle,"</b> go against the fundamental design of array-based structures Stack, Queue, and Ring Buffer. They are intentionally left out of these events to highlight the unique strengths of Linked Lists.</li>

            <li><b>Is a slow animation a bug?</b> No! When a Stack struggles with the <b>"Remove from Front"</b> scenario, the long, multi-step animation is the whole point. It's a visual lesson in LIFO vs. FIFO and shows why choosing the right data structure for the job is so important.</li>

            <li>If the visuals are too fast, click on <b>Replay Last</b> to see the previous animation.</li>
          </ul>
        </div>
      <div className={styles.controls}>
        <button onClick={() => handleAddLast(false)} disabled={isAnimating}>Add to End</button>
        <button onClick={() => handleAddFirst(false)} disabled={isAnimating}>Scenario: Add to Front</button>
        <button onClick={() => handleRemoveFromFront(false)} disabled={isAnimating}>Scenario: Remove from Front</button>
        <button onClick={() => handleRemoveFromEnd(false)} disabled={isAnimating}>Scenario: Remove from End</button>
        <button onClick={() => handleInsertInMiddle(false)} disabled={isAnimating}>Scenario: Insert in Middle</button>
        <button onClick={() => handleReset(false)} disabled={isAnimating}>Reset</button>
        <button onClick={handleReplay} disabled={isAnimating || !lastAction} className={styles.replayButton}>Replay Last</button>

      </div>

      <div className={styles.comparisonGrid}>
        <StackView command={stackCommand} onComplete={handleAnimationComplete} />
        <QueueView command={queueCommand} onComplete={handleAnimationComplete} />
        <LinkedListView command={linkedListCommand} onComplete={handleAnimationComplete} />
        <DoublyLinkedListView command={doublyLinkedListCommand} onComplete={handleAnimationComplete} />
        <RingBufferView command={ringBufferCommand} onComplete={handleAnimationComplete} />
      </div>
    </div>
  );
};

export default CompareLists;